import {
  ChatMentionsMenu,
  IconButton,
  Menu,
  createFindingMention,
  MentionsMenuRoot,
  MentionEntityMenuIcon,
  type ChatMentionsMenuItemType,
  type ChatMentionsMenuRef,
  type ChatMentionsMenuSection,
} from '@terra/ui-design-system';
import { useMentions } from '../../hooks/useMentions';
import { AtIcon } from '@terra/ui-icons';
import {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useCallback,
} from 'react';
import {
  useOptionalTargetId,
  useAppTargetId,
  useTargetType,
} from '../../store/projectStore';
import { useDebounceValue } from 'usehooks-ts';
import { useMentionsFindings } from '../../hooks/api/useMentionsFindings';
import { useMentionsSignals } from '../../hooks/api/useMentionsSignals';
import { normalizeHttpVersionNumber } from '@terra/shared-utils';
import { useQuery } from '@tanstack/react-query';
import { terraProxyApiClient } from '../../clients/testerClient';
import { useEndpoints } from '../../hooks/api/useEndpoints';
import { useExternalAssetMentionCategories } from '../../hooks/api/useExternalAssetMentionCategories';
import type { GroupedSignal } from '@terra/shared-contracts-signals';
import type { EndpointReturnDto } from '@terra/shared-contracts-endpoints';
import type { WebPageWithSignedScreenshotReturnDto } from '@terra/shared-contracts-web-pages';
import type { ApplicationNote } from '@terra/shared-contracts-applications';
import type { TracePreviewDto } from '@terra/shared-contracts-trace';
import { useProxyStore, type HttpRequest } from '../../store/proxy-store';
import { useReplaySessionStore } from '../../store/replaySessionStore';
import { createExternalNetworkMentionBaseSections } from '../../utils/externalAssetMentionUtils';
import { httpRequestToMentionMenuItem } from '../../utils/httpRequestToMentionMenuItem';
import { TARGET_TYPE } from '../../utils/target-scope.utils';

const createBaseSections = (): ChatMentionsMenuSection[] => [
  {
    id: 'main',
    title: '',
    items: [
      {
        id: 'httpRequest',
        label: 'HTTP Requests',
        icon: <MentionEntityMenuIcon entityType="HttpRequest" />,
        type: 'parent',
        children: [],
      },
      {
        id: 'endpoint',
        label: 'Endpoints',
        icon: <MentionEntityMenuIcon entityType="EndPoint" />,
        type: 'parent',
        children: [],
      },
      {
        id: 'webPage',
        label: 'Web Pages',
        icon: <MentionEntityMenuIcon entityType="WebPage" />,
        type: 'parent',
        children: [],
      },
      {
        id: 'execution',
        label: 'Executions',
        icon: <MentionEntityMenuIcon entityType="Trace" />,
        type: 'parent',
        children: [],
      },
      {
        id: 'signal',
        label: 'Signals',
        icon: <MentionEntityMenuIcon entityType="Signal" />,
        type: 'parent',
        children: [],
      },
      {
        id: 'finding',
        label: 'Findings',
        icon: <MentionEntityMenuIcon entityType="Finding" />,
        type: 'parent',
        children: [],
      },
      {
        id: 'testingInstruction',
        label: 'Testing Instructions',
        icon: <MentionEntityMenuIcon entityType="TestingInstruction" />,
        type: 'parent',
        children: [],
      },
    ],
  },
];
// Helper function to create submenu sections from parent item
const createSubmenuFromParent = (
  parentItem: ChatMentionsMenuItemType,
): ChatMentionsMenuSection[] => {
  if (!parentItem.children) return [];

  return [
    {
      id: `${parentItem.id}-children`,
      title: '',
      items: parentItem.children,
    },
  ];
};

// Convert signal to mention menu item
function signalToMentionMenuItem(
  signal: GroupedSignal,
): ChatMentionsMenuItemType {
  const label = signal.description || `Signal ${signal.id.slice(-4)}`;

  return {
    id: `signal-${signal.id}`,
    label,
    icon: <MentionEntityMenuIcon entityType="Signal" />,
    type: 'child',
    mentionEntity: {
      entityType: 'Signal',
      entityId: signal.id,
      label,
      extraData: { signal },
    },
  };
}

// Convert endpoint to mention menu item
function endpointToMentionMenuItem(
  endpoint: EndpointReturnDto,
): ChatMentionsMenuItemType {
  const method = endpoint?.request?.method || 'N/A';
  const urlPath = endpoint.url || endpoint.description || 'N/A';
  const label = `${method} ${urlPath}`;

  return {
    id: `endpoint-${endpoint.id}`,
    label,
    icon: <MentionEntityMenuIcon entityType="EndPoint" />,
    type: 'child',
    mentionEntity: {
      entityType: 'EndPoint',
      entityId: endpoint.id,
      label,
      extraData: { endpoint },
    },
  };
}

// Convert webpage to mention menu item
function webPageToMentionMenuItem(
  webPage: WebPageWithSignedScreenshotReturnDto,
): ChatMentionsMenuItemType {
  const label = webPage.description || webPage.url || 'Web Page';

  return {
    id: `webpage-${webPage.id}`,
    label,
    icon: <MentionEntityMenuIcon entityType="WebPage" />,
    type: 'child',
    mentionEntity: {
      entityType: 'WebPage',
      entityId: webPage.id,
      label,
      extraData: { webPage },
    },
  };
}

function traceToMentionMenuItem(
  trace: TracePreviewDto,
): ChatMentionsMenuItemType {
  const label =
    trace.signal?.description || `Trace ${trace.threadId?.slice(-8)}`;
  const displayLabel = label.length > 50 ? `${label.slice(0, 50)}...` : label;

  return {
    id: `executions-${trace.threadId}`,
    label: displayLabel,
    icon: <MentionEntityMenuIcon entityType="Trace" />,
    type: 'child',
    mentionEntity: {
      entityType: 'Trace',
      entityId: trace.threadId,
      label: displayLabel,
      extraData: { trace },
    },
  };
}

// Convert testing instruction (application note) to mention menu item
function testingInstructionToMentionMenuItem(
  note: ApplicationNote,
): ChatMentionsMenuItemType {
  const label = note.title || `Instruction ${note.id.slice(-4)}`;
  const displayLabel = label.length > 50 ? `${label.slice(0, 50)}...` : label;

  return {
    id: `testingInstruction-${note.id}`,
    label: displayLabel,
    icon: <MentionEntityMenuIcon entityType="TestingInstruction" />,
    type: 'child',
    mentionEntity: {
      entityType: 'TestingInstruction',
      entityId: note.id,
      label: displayLabel,
      extraData: { testingInstruction: note },
    },
  };
}

type MentionsMenuComposerProps = {
  onItemSelect?: (item: ChatMentionsMenuItemType) => void;
  mode: 'trigger' | 'inline';
  onClose?: () => void;
  getAnchorPosition?: () =>
    | {
        x: number;
        y: number;
      }
    | undefined;
};

export type MentionsMenuOpenScope = 'all' | 'objects' | 'testingInstruction';
export type MentionsMenuOpenOptions = {
  x?: number;
  y?: number;
  scope?: MentionsMenuOpenScope;
};
export type MentionsMenuComposerRef = {
  open: (options?: MentionsMenuOpenOptions) => void;
  close: () => void;
};
export const MentionsMenuComposer = forwardRef<
  MentionsMenuComposerRef,
  MentionsMenuComposerProps
>(({ onItemSelect, mode, onClose, getAnchorPosition }, ref) => {
  const targetId = useOptionalTargetId();
  const targetType = useTargetType();
  const appTargetId = useAppTargetId();
  const isExternalNetwork = targetType === TARGET_TYPE.EXTERNAL_NETWORK;
  const surfaceId = isExternalNetwork ? targetId : undefined;
  const mentionsRef = useRef<ChatMentionsMenuRef>(null);
  const { addMention } = useMentions();
  const [isOpen, setIsOpen] = useState(false);
  const [openScope, setOpenScope] = useState<MentionsMenuOpenScope>('all');
  const [position, setPosition] = useState<
    | {
        x: number;
        y: number;
      }
    | undefined
  >(undefined);
  const externalAssetCategories = useExternalAssetMentionCategories({
    surfaceId,
    enabled: isExternalNetwork && isOpen,
  });
  // Get recent HTTP requests from proxy store
  const { requests: proxyRequests, requestOrder } = useProxyStore();
  // Get all requests for search (up to 200 most recent)
  // The ChatMentionsMenu has built-in search that filters by label
  const allHttpRequests = useMemo(() => {
    const recentIds = requestOrder.slice(-200).reverse(); // Most recent first

    return recentIds
      .map((id) => proxyRequests[id])
      .filter((req): req is HttpRequest => !!req);
  }, [proxyRequests, requestOrder]);
  // Get active replay session request
  const { sessions, activeSessionId } = useReplaySessionStore();
  const activeSession = useMemo(
    () => sessions.find((s) => s.id === activeSessionId),
    [sessions, activeSessionId],
  );
  const { endpoints: endpointsData } = useEndpoints({
    applicationId: appTargetId,
    enabled: !!appTargetId && isOpen,
  });
  const { data: webPagesData } = useQuery({
    queryKey: ['webpages', appTargetId],
    queryFn: async () => {
      if (!appTargetId) return [];
      const result =
        await terraProxyApiClient.getWebPagesByApplication(appTargetId);

      return (result?.webPages || []) as WebPageWithSignedScreenshotReturnDto[];
    },
    enabled: !!appTargetId && isOpen,
    staleTime: 30000,
  });
  const { data: tracesData } = useQuery({
    queryKey: ['traces-mentions', appTargetId],
    queryFn: async () => {
      if (!appTargetId) return [];
      const response = await terraProxyApiClient.getTracesByApplication({
        applicationId: appTargetId,
        limit: 100,
      });

      if (response.status === 200) {
        return response.body.traces ?? [];
      }

      return [];
    },
    enabled: !!appTargetId && isOpen,
    staleTime: 30000,
  });
  const { data: testingInstructionsData } = useQuery({
    queryKey: ['testingInstructions', appTargetId],
    queryFn: async () => {
      if (!appTargetId) return [];
      const result = await terraProxyApiClient.getApplicationNotes(appTargetId);

      return (result?.notes || []) as ApplicationNote[];
    },
    enabled: !!appTargetId && isOpen,
    staleTime: 30000,
  });
  const endpoints = useMemo(() => endpointsData || [], [endpointsData]);
  const webPages = useMemo(() => webPagesData || [], [webPagesData]);
  const traces = useMemo(() => tracesData || [], [tracesData]);
  const testingInstructions = useMemo(
    () => testingInstructionsData || [],
    [testingInstructionsData],
  );
  const getAnchorPositionRef = useRef(getAnchorPosition);

  getAnchorPositionRef.current = getAnchorPosition;
  const [sections, setSections] =
    useState<ChatMentionsMenuSection[]>(createBaseSections());
  const [selectedItemId, setSelectedItemId] = useState<string | undefined>();
  const [currentSections, setCurrentSections] =
    useState<ChatMentionsMenuSection[]>(sections);
  const [isInSubmenu, setIsInSubmenu] = useState(false);
  const [currentParent, setCurrentParent] =
    useState<ChatMentionsMenuItemType | null>(null);
  const [submenuSearchQuery, setSubmenuSearchQuery] = useState('');
  const [debouncedSubmenuSearch] = useDebounceValue(submenuSearchQuery, 350);
  const mentionsSearchSessionKey = isInSubmenu
    ? `${currentParent?.id ?? 'submenu'}-${openScope}`
    : `root-${openScope}`;
  const serverMentionsSearchTerm =
    submenuSearchQuery === '' ? '' : debouncedSubmenuSearch;

  useEffect(() => {
    setSubmenuSearchQuery('');
  }, [mentionsSearchSessionKey]);

  const findingsMentionsEnabled =
    isOpen && !!targetId && isInSubmenu && currentParent?.id === 'finding';
  const signalsMentionsEnabled =
    isOpen && !!targetId && isInSubmenu && currentParent?.id === 'signal';
  const {
    findings: mentionsFindings,
    isLoading: isFindingsMentionsLoading,
    isFetchingNextPage: isFindingsMentionsFetchingNextPage,
    hasNextPage: hasMoreFindingsMentions,
    fetchNextPage: fetchNextFindingsMentionsPage,
  } = useMentionsFindings({
    targetId,
    targetType,
    searchTerm: serverMentionsSearchTerm,
    enabled: findingsMentionsEnabled,
  });
  const {
    signals: mentionsSignals,
    isLoading: isSignalsMentionsLoading,
    isFetchingNextPage: isSignalsMentionsFetchingNextPage,
    hasNextPage: hasMoreSignalsMentions,
    fetchNextPage: fetchNextSignalsMentionsPage,
  } = useMentionsSignals({
    targetId,
    targetType,
    searchTerm: serverMentionsSearchTerm,
    enabled: signalsMentionsEnabled,
  });
  const isServerMentionsSearch =
    findingsMentionsEnabled || signalsMentionsEnabled;
  const isMentionsListLoading =
    (findingsMentionsEnabled && isFindingsMentionsLoading) ||
    (signalsMentionsEnabled && isSignalsMentionsLoading);
  const isMentionsLoadingMore =
    (findingsMentionsEnabled && isFindingsMentionsFetchingNextPage) ||
    (signalsMentionsEnabled && isSignalsMentionsFetchingNextPage);
  const canLoadMoreMentions =
    (findingsMentionsEnabled && hasMoreFindingsMentions) ||
    (signalsMentionsEnabled && hasMoreSignalsMentions);
  const handleMentionsLoadMore = useCallback(() => {
    if (
      findingsMentionsEnabled &&
      hasMoreFindingsMentions &&
      !isFindingsMentionsFetchingNextPage
    ) {
      void fetchNextFindingsMentionsPage();

      return;
    }

    if (
      signalsMentionsEnabled &&
      hasMoreSignalsMentions &&
      !isSignalsMentionsFetchingNextPage
    ) {
      void fetchNextSignalsMentionsPage();
    }
  }, [
    fetchNextFindingsMentionsPage,
    fetchNextSignalsMentionsPage,
    findingsMentionsEnabled,
    hasMoreFindingsMentions,
    isFindingsMentionsFetchingNextPage,
    hasMoreSignalsMentions,
    isSignalsMentionsFetchingNextPage,
    signalsMentionsEnabled,
  ]);
  const isInSubmenuRef = useRef(false);
  const currentParentIdRef = useRef<string | undefined>(undefined);
  const sectionsRef = useRef(sections);

  isInSubmenuRef.current = isInSubmenu;
  currentParentIdRef.current = currentParent?.id;
  sectionsRef.current = sections;
  const resetMentionsMenuNavigation = () => {
    setIsInSubmenu(false);
    setCurrentSections(sectionsRef.current);
    setSelectedItemId(undefined);
    setCurrentParent(null);
    setOpenScope('all');
    setSubmenuSearchQuery('');
  };

  const handleOpenChange = (details: { open: boolean }) => {
    if (!details.open) {
      resetMentionsMenuNavigation();
      setIsOpen(false);
      setPosition(undefined);
      onClose?.();

      return;
    }

    setIsOpen(true);
  };

  useImperativeHandle(
    ref,
    () => ({
      open: (options?: MentionsMenuOpenOptions) => {
        const scope = options?.scope ?? 'all';

        setOpenScope(scope);
        setIsInSubmenu(false);
        setCurrentParent(null);
        setSelectedItemId(undefined);
        const openAt = (anchor: { x: number; y: number }) => {
          setPosition(anchor);
          setIsOpen(true);
        };

        if (options?.x != null && options?.y != null) {
          openAt({ x: options.x, y: options.y });

          return;
        }

        requestAnimationFrame(() => {
          const resolved = getAnchorPositionRef.current?.();

          if (resolved) {
            openAt(resolved);

            return;
          }

          openAt({
            x: window.innerWidth / 2 - 120,
            y: window.innerHeight - 48,
          });
        });
      },
      close: () => {
        resetMentionsMenuNavigation();
        setIsOpen(false);
        setPosition(undefined);
        onClose?.();
      },
    }),
    [onClose],
  );
  useEffect(() => {
    const cloned = isExternalNetwork
      ? createExternalNetworkMentionBaseSections(externalAssetCategories)
      : createBaseSections();
    const mainSection = cloned[0];

    if (mainSection && Array.isArray(mainSection.items)) {
      for (const item of mainSection.items) {
        if (!isExternalNetwork && item.id === 'httpRequest') {
          const httpItems: ChatMentionsMenuItemType[] = [];

          if (activeSession) {
            const replayRequest: HttpRequest = {
              id: `replay-${activeSession.id}`,
              sequentialId: 0,
              method: activeSession.request.method,
              url: activeSession.request.url,
              host: '',
              headers: activeSession.request.headers as Record<string, string>,
              body: activeSession.request.body || '',
              timestamp: new Date(),
              source: 'replay',
              httpVersion: normalizeHttpVersionNumber(
                activeSession.protocol === 'http2' ? '2.0' : '1.1',
              ),
            };
            const replayItem = httpRequestToMentionMenuItem(
              replayRequest,
              'replay',
            );

            if (replayItem) {
              replayItem.label = `[Replay] ${replayItem.label}`;
              httpItems.push(replayItem);
            }
          }

          const globalItems = allHttpRequests
            .map((req) => httpRequestToMentionMenuItem(req, 'global'))
            .filter((item): item is ChatMentionsMenuItemType => item !== null);

          httpItems.push(...globalItems);
          if (httpItems.length > 0) {
            item.hasSubmenu = true;
            item.children = httpItems;
          } else {
            item.hasSubmenu = false;
            item.children = [];
          }
        }

        if (!isExternalNetwork && item.id === 'endpoint') {
          if (endpoints.length > 0) {
            item.hasSubmenu = true;
            item.children = endpoints.map((endpoint) =>
              endpointToMentionMenuItem(endpoint),
            );
          } else {
            item.hasSubmenu = false;
            item.children = [];
          }
        }

        if (!isExternalNetwork && item.id === 'webPage') {
          if (webPages.length > 0) {
            item.hasSubmenu = true;
            item.children = webPages.map((webPage) =>
              webPageToMentionMenuItem(webPage),
            );
          } else {
            item.hasSubmenu = false;
            item.children = [];
          }
        }

        if (!isExternalNetwork && item.id === 'execution') {
          const uniqueTraces = traces.filter(
            (trace, index, arr) =>
              arr.findIndex((t) => t.threadId === trace.threadId) === index,
          );

          if (uniqueTraces.length > 0) {
            item.hasSubmenu = true;
            item.children = uniqueTraces.map((trace) =>
              traceToMentionMenuItem(trace),
            );
          } else {
            item.hasSubmenu = false;
            item.children = [];
          }
        }

        if (item.id === 'signal') {
          item.hasSubmenu = Boolean(targetId);
          item.children = mentionsSignals.map((signal) =>
            signalToMentionMenuItem(signal),
          );
        }

        if (item.id === 'finding') {
          item.hasSubmenu = Boolean(targetId);
          item.children = mentionsFindings.map((finding) =>
            createFindingMention(finding),
          );
        }

        if (!isExternalNetwork && item.id === 'testingInstruction') {
          if (testingInstructions.length > 0) {
            item.hasSubmenu = true;
            item.children = testingInstructions.map((instruction) =>
              testingInstructionToMentionMenuItem(instruction),
            );
          } else {
            item.hasSubmenu = false;
            item.children = [];
          }
        }
      }
    }

    const scopedSections =
      openScope === 'objects'
        ? cloned.map((section) => ({
            ...section,
            items: section.items.filter(
              (item) => item.id !== 'testingInstruction',
            ),
          }))
        : cloned;

    setSections(scopedSections);
    if (openScope === 'testingInstruction') {
      const testingItem = cloned[0]?.items.find(
        (item) => item.id === 'testingInstruction',
      );

      if (testingItem) {
        setCurrentParent(testingItem);
        setCurrentSections(createSubmenuFromParent(testingItem));
        setIsInSubmenu(true);

        return;
      }
    }

    const parentId = currentParentIdRef.current;

    if (isInSubmenuRef.current && parentId) {
      const updatedParent = scopedSections[0]?.items.find(
        (item) => item.id === parentId,
      );

      if (updatedParent) {
        const keepServerMentionsSubmenu =
          (parentId === 'finding' || parentId === 'signal') &&
          Boolean(targetId);
        const hasChildren = (updatedParent.children?.length ?? 0) > 0;

        if (hasChildren || keepServerMentionsSubmenu) {
          setCurrentParent(updatedParent);
          setCurrentSections(createSubmenuFromParent(updatedParent));

          return;
        }
      }
    }

    setCurrentParent(null);
    setIsInSubmenu(false);
    setCurrentSections(scopedSections);
  }, [
    isExternalNetwork,
    externalAssetCategories,
    allHttpRequests,
    activeSession,
    endpoints,
    webPages,
    traces,
    mentionsSignals,
    mentionsFindings,
    testingInstructions,
    openScope,
    targetId,
  ]);
  const handleItemSelect = (item: ChatMentionsMenuItemType) => {
    onItemSelect?.(item);
    addMention(item);
    handleClose();
  };

  const handleNavigateToSubmenu = (parentItem: ChatMentionsMenuItemType) => {
    setSubmenuSearchQuery('');
    setCurrentParent(parentItem);
    setCurrentSections(createSubmenuFromParent(parentItem));
    setIsInSubmenu(true);
  };

  const handleBack = () => {
    if (openScope === 'testingInstruction') {
      setOpenScope('all');
    }

    setSubmenuSearchQuery('');
    setCurrentSections(sections);
    setIsInSubmenu(false);
    setCurrentParent(null);
  };

  const handleClose = () => {
    resetMentionsMenuNavigation();
    setIsOpen(false);
    setPosition(undefined);
    onClose?.();
  };

  const menuContent = (
    <ChatMentionsMenu
      ref={mentionsRef}
      sections={currentSections}
      onItemSelect={handleItemSelect}
      selectedItemId={selectedItemId}
      onClose={handleClose}
      onBack={handleBack}
      showBackButton={isInSubmenu}
      onNavigateToSubmenu={handleNavigateToSubmenu}
      searchPlaceholder={
        isInSubmenu
          ? `Search ${currentParent?.label || 'items'}...`
          : 'Search mentions...'
      }
      mode={mode === 'inline' ? 'compact' : 'default'}
      searchFiltering={isServerMentionsSearch ? 'server' : 'client'}
      onSearchQueryChange={
        isInSubmenu && (findingsMentionsEnabled || signalsMentionsEnabled)
          ? setSubmenuSearchQuery
          : undefined
      }
      searchSessionKey={mentionsSearchSessionKey}
      onLoadMore={handleMentionsLoadMore}
      canLoadMore={canLoadMoreMentions}
      isLoadingMore={isMentionsLoadingMore}
      isLoadingList={isMentionsListLoading}
    />
  );

  return (
    <MentionsMenuRoot
      open={isOpen}
      onOpenChange={handleOpenChange}
      position={position}
      trigger={
        mode === 'trigger' ? (
          <IconButton variant="ghost" size="sm" aria-label="Mentions">
            <AtIcon />
          </IconButton>
        ) : undefined
      }
    >
      {mode === 'trigger' ? (
        <Menu.Positioner>
          <Menu.Content
            minW="240px"
            maxW="320px"
            maxH="320px"
            overflow="hidden"
            p={0}
            w="auto"
          >
            {menuContent}
          </Menu.Content>
        </Menu.Positioner>
      ) : (
        menuContent
      )}
    </MentionsMenuRoot>
  );
});
MentionsMenuComposer.displayName = 'MentionsMenuComposer';
